// 261010 P9D D7 — hàm THUẦN cho cảnh báo ở bước 3 (dàn cảnh, liền mạch) và bước 7 (stale, lệch bộ đồ, lint prompt). 0 antd/router.
import type { FilmEvent } from '../../types/film';
import { stagingChipLabel, stagingChipTone, stagingSummary } from './stagingStatusUtils';

/** Chip «Dàn cảnh x/y» đầu bước 3 — CÙNG nguồn với PipelineStepper (`stagingSummary`). Plan không có `stagingVersion` / 0 video ⇒ null. */
export function stagingChipOf(events: Array<{ staging?: unknown }> | null | undefined, stagingVersion?: string | null): { label: string; tone: 'ok' | 'warn' } | null {
  const s = stagingSummary(events ?? [], stagingVersion);
  if (!s.show || s.total === 0) return null;
  return { label: stagingChipLabel(s), tone: stagingChipTone(s) };
}

/** Cảnh báo liền mạch của video (`continuityLedger.warnings`) — chuỗi không rỗng, bỏ trùng. */
export function continuityWarningsOf(ev: Pick<FilmEvent, 'continuityLedger'> | null | undefined): string[] {
  const w = (ev?.continuityLedger as { warnings?: unknown } | null | undefined)?.warnings;
  if (!Array.isArray(w)) return [];
  return Array.from(new Set(w.filter((x): x is string => typeof x === 'string').map((x) => x.trim()).filter(Boolean)));
}

const STALE_LABEL: Record<string, string> = { prompt: 'prompt', clip: 'clip', continuity: 'sổ liền mạch' };
/** «Vì sao đã cũ» cho tooltip bước 7/8: từ `staleStages` của video. Rỗng ⇒ null. Mã lạ giữ nguyên. */
export function staleNoteOf(stages: string[] | null | undefined): string | null {
  const list = (stages ?? []).filter(Boolean);
  if (!list.length) return null;
  return `Đã cũ vì phần phía trước vừa đổi: ${list.map((s) => STALE_LABEL[s] ?? s).join(', ')}. Dựng lại / gen lại ở bước tương ứng.`;
}

/** Lệch bộ đồ: `plan.lookMismatchEventIds` (BE tính lúc đọc) + `ev.promptLookMismatch`. */
export function lookMismatchOf(
  plan: { lookMismatchEventIds?: string[] | null } | null | undefined,
  ev: Pick<FilmEvent, 'id' | 'promptLookMismatch'> | null | undefined,
): { count: number; ids: string[]; thisEvent: boolean; names: string[] } {
  const ids = Array.isArray(plan?.lookMismatchEventIds) ? (plan!.lookMismatchEventIds as string[]) : [];
  const m = ev?.promptLookMismatch;
  const thisEvent = !!m?.mismatch;
  return { count: ids.length, ids, thisEvent, names: thisEvent ? (m?.characterNames ?? []) : [] };
}

export interface LintFindingLite { severity: 'error' | 'warn' | 'info'; code: string; ref: string; message: string }
/** Lint cấp plan → riêng video số `eventOrder`: ref có phần `E<n>` (S2·E5, S2·E5·shot2, S3·E5·#4) khớp đúng n (E1 ≠ E12). Lỗi trước warn; bỏ info. */
export function lintForEventOf(findings: LintFindingLite[] | null | undefined, eventOrder: number): LintFindingLite[] {
  const re = new RegExp(`(^|·)E${eventOrder}(·|$)`);
  const hit = (findings ?? []).filter((f) => f.severity !== 'info' && re.test(f.ref));
  return [...hit.filter((f) => f.severity === 'error'), ...hit.filter((f) => f.severity !== 'error')];
}

// ───────────── PR10: sổ liền mạch (trạng thái SAU video) — tách khỏi EV33 (cảnh báo) ─────────────
export interface LedgerView {
  characters: Array<{ name: string; position: string; holding: string[]; mood: string }>;
  setting: { location: string; timeOfDay: string } | null;
}
/** Đọc `continuityLedger.after`: vị trí · vật đang cầm · tâm trạng từng nhân vật + địa điểm/giờ. Không có ledger/`after` rỗng hẳn ⇒ null (empty state riêng, KHÔNG gộp với "không có cảnh báo"). */
export function ledgerViewOf(ev: Pick<FilmEvent, 'continuityLedger'> | null | undefined): LedgerView | null {
  const after = (ev?.continuityLedger as { after?: { characters?: unknown; setting?: unknown } } | null | undefined)?.after;
  if (!after || typeof after !== 'object') return null;
  const chars = (Array.isArray(after.characters) ? after.characters : []).filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => ({
      name: String(c.name ?? '').trim(),
      position: String(c.position ?? '').trim(),
      holding: Array.isArray(c.holding) ? (c.holding as unknown[]).map((h) => String(h).trim()).filter(Boolean) : [],
      mood: String(c.mood ?? '').trim(),
    })).filter((c) => c.name);
  const st = after.setting as { location?: unknown; timeOfDay?: unknown } | null | undefined;
  const setting = st && typeof st === 'object' && (String(st.location ?? '').trim() || String(st.timeOfDay ?? '').trim())
    ? { location: String(st.location ?? '').trim(), timeOfDay: String(st.timeOfDay ?? '').trim() } : null;
  return chars.length || setting ? { characters: chars, setting } : null;
}
